import Phaser from "phaser";
import { applySpriteOrigin, assetOrigin } from "../assets/loader";
import { addBlob, addShadowBlob } from "../assets/primitives";
import { isFoodKind } from "../data/encounterPatterns";
import { textureForKind, type ScheduledEncounter } from "./EncounterDirector";

export interface WorldSprite {
  encounter: ScheduledEncounter;
  sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image | Phaser.GameObjects.Container;
  shadow: Phaser.GameObjects.Image;
  glint?: Phaser.GameObjects.Image;
  warned: boolean;
  dodgePlayed: boolean;
  wolfAnim?: string;
  animEpoch: number;
}

const UNUSED: ScheduledEncounter = {
  id: "",
  kind: "apple",
  lane: 0,
  reachAtMs: 0,
  leadMs: 0,
  consumed: true,
};

const WOLF_PREWARM = 4;
const RAVINE_PREWARM = 3;
const IMAGE_PREWARM = 12;
const GLINT_PREWARM = 4;

export class WorldSpritePool {
  private readonly wolves: WorldSprite[] = [];
  private readonly ravines: WorldSprite[] = [];
  private readonly images: WorldSprite[] = [];
  private readonly glints: Phaser.GameObjects.Image[] = [];
  private readonly created: WorldSprite[] = [];
  private readonly createdGlints: Phaser.GameObjects.Image[] = [];
  private readonly live = new Set<WorldSprite>();
  private tornDown = false;

  constructor(private readonly scene: Phaser.Scene) {
    for (let i = 0; i < WOLF_PREWARM; i += 1) {
      this.wolves.push(this.createWolf());
    }
    for (let i = 0; i < RAVINE_PREWARM; i += 1) {
      this.ravines.push(this.createRavine());
    }
    for (let i = 0; i < IMAGE_PREWARM; i += 1) {
      this.images.push(this.createImage());
    }
    if (scene.textures.exists("food-golden-glint")) {
      for (let i = 0; i < GLINT_PREWARM; i += 1) {
        this.glints.push(this.createGlint());
      }
    }
  }

  acquire(encounter: ScheduledEncounter): WorldSprite {
    if (this.tornDown) {
      throw new Error("WorldSpritePool.acquire after destroy");
    }
    const item =
      encounter.kind === "wolf"
        ? (this.wolves.pop() ?? this.createWolf())
        : encounter.kind === "ravine"
          ? (this.ravines.pop() ?? this.createRavine())
          : (this.images.pop() ?? this.createImage());
    this.live.add(item);
    this.resetItem(item, encounter);
    return item;
  }

  release(item: WorldSprite): void {
    if (this.tornDown || !this.live.delete(item)) {
      return;
    }
    this.parkItem(item);
    if (item.sprite instanceof Phaser.GameObjects.Sprite) {
      this.wolves.push(item);
    } else if (item.sprite instanceof Phaser.GameObjects.Container) {
      this.ravines.push(item);
    } else {
      this.images.push(item);
    }
  }

  destroy(): void {
    if (this.tornDown) {
      return;
    }
    this.tornDown = true;
    for (const item of this.created) {
      this.destroyObject(item.sprite);
      this.destroyObject(item.shadow);
    }
    for (const glint of this.createdGlints) {
      this.destroyObject(glint);
    }
    this.created.length = 0;
    this.createdGlints.length = 0;
    this.wolves.length = 0;
    this.ravines.length = 0;
    this.images.length = 0;
    this.glints.length = 0;
    this.live.clear();
  }

  private resetItem(item: WorldSprite, encounter: ScheduledEncounter): void {
    if (item.glint) {
      this.scene.tweens.killTweensOf(item.glint);
      item.glint.setActive(false).setVisible(false);
      this.glints.push(item.glint);
      item.glint = undefined;
    }
    item.encounter = encounter;
    item.warned = false;
    item.dodgePlayed = false;
    item.animEpoch += 1;
    item.wolfAnim = encounter.kind === "wolf" ? "wolf-run-toward" : undefined;
    item.shadow.setActive(true).setVisible(true);
    item.shadow.setAlpha(0.28);
    item.shadow.clearTint();

    if (item.sprite instanceof Phaser.GameObjects.Sprite) {
      const wolf = item.sprite;
      this.scene.tweens.killTweensOf(wolf);
      wolf.off(Phaser.Animations.Events.ANIMATION_COMPLETE);
      wolf.clearTint();
      wolf.setAlpha(1);
      wolf.setScale(1);
      wolf.setAngle(0);
      wolf.setRotation(0);
      wolf.setActive(true).setVisible(true);
      wolf.play("wolf-run-toward");
      applySpriteOrigin(wolf);
      item.wolfAnim = "wolf-run-toward";
    } else if (item.sprite instanceof Phaser.GameObjects.Container) {
      const ravine = item.sprite;
      this.scene.tweens.killTweensOf(ravine);
      ravine.setAlpha(1);
      ravine.setScale(1);
      ravine.setAngle(0);
      ravine.setActive(true).setVisible(true);
    } else {
      const image = item.sprite;
      const texture = textureForKind(encounter.kind);
      const origin = assetOrigin(texture);
      this.scene.tweens.killTweensOf(image);
      image.setTexture(texture);
      image.clearTint();
      image.setAlpha(1);
      image.setScale(1);
      image.setAngle(0);
      if (isFoodKind(encounter.kind)) {
        image.setOrigin(0.5, 0.82);
      } else {
        image.setOrigin(origin.x, origin.y);
      }
      image.setActive(true).setVisible(true);
    }

    if ((encounter.kind === "honey" || encounter.kind === "fish") && this.scene.textures.exists("food-golden-glint")) {
      const glint = this.glints.pop() ?? this.createGlint();
      glint.setAlpha(0.85);
      glint.setScale(1);
      glint.setAngle(0);
      glint.setActive(true).setVisible(true);
      item.glint = glint;
    }
  }

  private parkItem(item: WorldSprite): void {
    item.animEpoch += 1;
    item.warned = false;
    item.dodgePlayed = false;
    item.wolfAnim = undefined;
    if (!item.sprite.scene) {
      item.glint = undefined;
      return;
    }
    this.scene.tweens.killTweensOf(item.sprite);
    this.scene.tweens.killTweensOf(item.shadow);
    if (item.sprite instanceof Phaser.GameObjects.Sprite && item.sprite.anims) {
      item.sprite.off(Phaser.Animations.Events.ANIMATION_COMPLETE);
      item.sprite.anims.stop();
      item.sprite.clearTint();
    }
    item.sprite.setActive(false).setVisible(false);
    if (item.shadow.scene) {
      item.shadow.setActive(false).setVisible(false);
    }
    if (item.glint?.scene) {
      this.scene.tweens.killTweensOf(item.glint);
      item.glint.setActive(false).setVisible(false);
      this.glints.push(item.glint);
      item.glint = undefined;
    } else {
      item.glint = undefined;
    }
  }

  private createWolf(): WorldSprite {
    const sprite = this.scene.add.sprite(0, 0, "wolf-run-toward", 0);
    sprite.setActive(false).setVisible(false);
    return this.track({
      encounter: UNUSED,
      sprite,
      shadow: this.hiddenShadow(),
      warned: false,
      dodgePlayed: false,
      wolfAnim: "wolf-run-toward",
      animEpoch: 0,
    });
  }

  private createRavine(): WorldSprite {
    const sprite = makeRavine(this.scene);
    sprite.setActive(false).setVisible(false);
    return this.track({
      encounter: UNUSED,
      sprite,
      shadow: this.hiddenShadow(),
      warned: false,
      dodgePlayed: false,
      animEpoch: 0,
    });
  }

  private createImage(): WorldSprite {
    const sprite = this.scene.add.image(0, 0, "food-apple");
    sprite.setActive(false).setVisible(false);
    return this.track({
      encounter: UNUSED,
      sprite,
      shadow: this.hiddenShadow(),
      warned: false,
      dodgePlayed: false,
      animEpoch: 0,
    });
  }

  private createGlint(): Phaser.GameObjects.Image {
    const glint = this.scene.add.image(0, 0, "food-golden-glint");
    glint.setActive(false).setVisible(false);
    this.createdGlints.push(glint);
    return glint;
  }

  private hiddenShadow(): Phaser.GameObjects.Image {
    const shadow = addShadowBlob(this.scene, 0, 0, 64, 18, 0.28);
    shadow.setActive(false).setVisible(false);
    return shadow;
  }

  private track(item: WorldSprite): WorldSprite {
    this.created.push(item);
    return item;
  }

  private destroyObject(obj: Phaser.GameObjects.GameObject): void {
    if (!obj.scene) {
      return;
    }
    this.scene.tweens.killTweensOf(obj);
    if (obj instanceof Phaser.GameObjects.Sprite && obj.anims) {
      obj.off(Phaser.Animations.Events.ANIMATION_COMPLETE);
      obj.anims.stop();
    }
    obj.destroy();
  }
}

function makeRavine(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const cast = addBlob(scene, 2, 10, 132, 38, 0x1a120c, 0.38);
  const soil = addBlob(scene, 0, 2, 128, 46, 0x7a5a38, 1);
  const rim = addBlob(scene, 0, 0, 118, 38, 0x5c3a1e, 1);
  const wall = addBlob(scene, 0, -3, 100, 28, 0x3a2416, 1);
  const hole = addBlob(scene, 0, -5, 84, 20, 0x120a08, 1);
  const core = addBlob(scene, 0, -6, 58, 12, 0x070403, 0.92);
  const nearLip = addBlob(scene, 0, 11, 110, 14, 0x6b4e30, 0.95);
  const nearHighlight = addBlob(scene, 0, 8, 86, 6, 0x9a7348, 0.35);
  const stonesL = addBlob(scene, -42, 8, 16, 8, 0x8a8680, 0.7);
  const stonesR = addBlob(scene, 40, 9, 14, 7, 0x7a7670, 0.65);
  return scene.add.container(0, 0, [cast, soil, rim, wall, hole, core, stonesL, stonesR, nearLip, nearHighlight]);
}
